const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface RequestOptions extends RequestInit {
  params?: Record<string, string>
}

let getTokenFn: (() => string | null) | null = null
let onUnauthorizedFn: (() => Promise<string | null>) | null = null

export function setAuthTokenProvider(fn: () => string | null) {
  getTokenFn = fn
}

/**
 * Called once per request that comes back 401: renew the session (single-flight, in
 * AuthProvider) and return the fresh access token, or null when the session is over — in
 * which case AuthProvider has already dropped the user and the sign-in screen replaces the
 * page, which stops its polling.
 */
export function setUnauthorizedHandler(fn: (() => Promise<string | null>) | null) {
  onUnauthorizedFn = fn
}

/**
 * fetch with the bearer token, and ONE renew-and-retry on 401. Before (2026-10-01) an expired
 * token was sent on every poll and each 401 was retried 3x by React Query: one open admin tab
 * produced ~34 rejected requests a minute and never showed the sign-in screen.
 */
async function authedFetch(url: string, init: RequestInit, headers: Record<string, string>): Promise<Response> {
  const send = (token: string | null | undefined) =>
    fetch(url, { ...init, headers: token ? { ...headers, Authorization: `Bearer ${token}` } : headers })

  const response = await send(getTokenFn?.())
  if (response.status !== 401 || !onUnauthorizedFn) return response
  const fresh = await onUnauthorizedFn()
  return fresh ? send(fresh) : response
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options
  let url = `${API_BASE}${path}`

  if (params) {
    const searchParams = new URLSearchParams(params)
    url += `?${searchParams.toString()}`
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Tag admin-console requests so auth events (e.g. change-password) are
    // attributed to the 'admin' surface in the login-audit trail.
    'X-Client-Surface': 'admin',
    ...(fetchOptions.headers as Record<string, string>),
  }

  const response = await authedFetch(url, fetchOptions, headers)

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '')
    throw new ApiError(response.status, response.statusText, errorBody)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: string,
  ) {
    super(`API Error ${status}: ${statusText}`)
    this.name = 'ApiError'
  }
}

async function requestFormData<T>(path: string, formData: FormData, method = 'POST'): Promise<T> {
  const url = `${API_BASE}${path}`

  const headers: Record<string, string> = {}
  // Do NOT set Content-Type — browser will set multipart boundary automatically

  const response = await authedFetch(url, { method, body: formData }, headers)

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '')
    throw new ApiError(response.status, response.statusText, errorBody)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

async function requestBlob(path: string): Promise<Blob> {
  const url = `${API_BASE}${path}`

  const headers: Record<string, string> = {}

  const response = await authedFetch(url, { method: 'GET' }, headers)

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '')
    throw new ApiError(response.status, response.statusText, errorBody)
  }

  return response.blob()
}

export const api = {
  get: <T>(path: string, params?: Record<string, string>) =>
    request<T>(path, { method: 'GET', params }),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),

  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData) =>
    requestFormData<T>(path, formData),

  blob: (path: string) =>
    requestBlob(path),
}
